<script setup lang="ts">
  // Ported from cartadigital-pos app/components/pos/main/PosMainOrdersList.vue.
  // Data source is ordersStore.pendingOrdersByType (socket-fed) instead of the
  // -pos offline read model; clicks resolve back to the store IOrder.
  import { computed } from 'vue';
  import { OrderType } from '@/enums/OrderEnums';
  import { IOrder } from '@/interfaces/IOrder';
  import {
    groupOrdersByStatus,
    IOrderGroup,
  } from '@/domain/pos/order-grouping';
  import { orderToListItem } from '@/domain/pos/order-formatting';
  import type { IOrderListItem } from '@/domain/pos/pos-types';
  import { getPosOrderTypeIcon } from '@/domain/pos/order-type-ui';
  import { useOrdersStore } from '@/stores/orders.store';
  import { orderMatchesSearch } from '@/utils/orders.utils';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiScrollable from '@/components/pos-v2/ui/PosUiScrollable.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import PosV2OrdersGroup from './PosV2OrdersGroup.vue';

  const props = defineProps<{
    orderType: OrderType;
    search: string;
  }>();

  const emit = defineEmits<{
    (e: 'order-click', order: IOrder): void;
    (e: 'order-preview', order: IOrder): void;
    (e: 'clear-search'): void;
  }>();

  const ordersStore = useOrdersStore();

  const isLoading = computed(() => !ordersStore.pendingOrdersByType);

  const orders = computed<IOrder[]>(
    () => ordersStore.pendingOrdersByType?.[props.orderType] ?? []
  );

  const filteredOrders = computed<IOrder[]>(() => {
    const q = props.search.trim();
    if (!q) return orders.value;
    return orders.value.filter((order) => orderMatchesSearch(order, q));
  });

  const orderGroups = computed<IOrderGroup[]>(() =>
    groupOrdersByStatus(
      filteredOrders.value.map(orderToListItem),
      props.orderType
    )
  );

  const titleInfo = computed(() => {
    if (props.orderType === OrderType.Away) {
      return {
        icon: getPosOrderTypeIcon(OrderType.Away),
        labelKey: 'posV2.ordersAway',
      };
    }
    if (props.orderType === OrderType.Delivery) {
      return {
        icon: getPosOrderTypeIcon(OrderType.Delivery),
        labelKey: 'posV2.ordersDelivery',
      };
    }
    return {
      icon: getPosOrderTypeIcon(props.orderType),
      labelKey: 'posV2.orders',
    };
  });

  function handleOrderClick(item: IOrderListItem) {
    const order = orders.value.find((o) => o.id === item.id);
    if (order) emit('order-click', order);
  }

  function handleOrderPreview(item: IOrderListItem) {
    const order = orders.value.find((o) => o.id === item.id);
    if (order) emit('order-preview', order);
  }
</script>

<template>
  <div class="pos-v2-orders-list">
    <div class="list-header">
      <h3 class="list-title">
        <PosUiIcon :name="titleInfo.icon" class="list-title-icon" />
        <span class="list-title-text">{{ $t(titleInfo.labelKey) }}</span>
      </h3>
    </div>

    <div v-if="isLoading" class="loading-state">
      <div class="loading-grid">
        <PosUiSkeleton v-for="i in 6" :key="i" class="order-skeleton" />
      </div>
    </div>

    <PosUiEmptyState
      v-else-if="!orders.length"
      :icon="titleInfo.icon"
      :title="$t('posV2.noActiveOrders')"
      class="empty-state"
    />

    <PosUiEmptyState
      v-else-if="!filteredOrders.length"
      icon="magnify"
      :title="$t('posV2.noResults')"
      class="empty-state"
    >
      <template #action>
        <PosUiButton
          color="neutral"
          variant="outline"
          size="lg"
          icon="close"
          @click="emit('clear-search')"
        >
          {{ $t('posV2.clearSearch') }}
        </PosUiButton>
      </template>
    </PosUiEmptyState>

    <PosUiScrollable
      v-else
      class="orders-container"
      orientation="vertical"
      variant="overlay"
      theme="page"
      size="sm"
      scroll-class="orders-scroll"
    >
      <PosV2OrdersGroup
        v-for="group in orderGroups"
        :key="group.status"
        :group="group"
        @order-click="handleOrderClick"
        @order-preview="handleOrderPreview"
      />
    </PosUiScrollable>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-orders-list {
    @apply tw-flex tw-h-full tw-flex-col tw-overflow-hidden;

    .list-header {
      @apply tw-border-b tw-border-neutral-200 tw-bg-white tw-px-4 tw-py-2.5;

      .list-title {
        @apply tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-neutral-900;

        .list-title-icon {
          @apply tw-flex-none tw-text-lg tw-text-neutral-500;
        }

        .list-title-text {
          @apply tw-min-w-0 tw-truncate;
        }
      }
    }

    .loading-state {
      @apply tw-p-4;

      .loading-grid {
        @apply tw-grid tw-gap-3;
        grid-template-columns: repeat(auto-fill, minmax(min(290px, 100%), 1fr));

        .order-skeleton {
          @apply tw-h-24 tw-rounded-xl;
        }
      }
    }

    .empty-state {
      @apply tw-flex-1;
    }

    .orders-container {
      @apply tw-relative tw-min-h-0 tw-flex-1;

      :deep(.orders-scroll) {
        @apply tw-flex tw-h-full tw-flex-col tw-gap-5 tw-overflow-y-auto tw-p-4;
      }
    }
  }
</style>
